import type { ComponentPropsWithoutRef, Dispatch, SetStateAction } from 'react';
import { Fragment, useCallback, useEffect } from 'react';
// Types
import type { PopperProps, usePopper } from 'react-popper';

import { Transition } from '@headlessui/react';
import clsx from 'clsx';

// Components
import Icon from '@/components/base/Icon';

const arrowStyle = {
    top: {
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid currentColor'
    },
    bottom: {
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderBottom: '6px solid currentColor'
    },
    left: {
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        borderLeft: '6px solid currentColor'
    },
    right: {
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        borderRight: '6px solid currentColor'
    }
} as const;

const arrowVariant = {
    default: 'text-white dark:text-icoDarkMode-anthracite',
    panel: 'text-icoGray-100 dark:text-icoDarkMode-deepBlack'
} as const;

export type CustomPopoverArrowProps = Pick<CustomPopoverProps, 'isBorder'> & {
    position?: PopperProps<unknown>['placement'];
    variant?: keyof typeof arrowVariant;
    customClassName: CustomPopoverProps['customArrowClassName'];
};

export const Arrow = ({
    position = 'bottom',
    isBorder = false,
    variant = 'default',
    customClassName
}: CustomPopoverArrowProps) => {
    const mainPosition = position.split('-')[0];
    const secondPosition = position.split('-')[1];
    const isTop = mainPosition === 'top';
    const isBottom = mainPosition === 'bottom';
    const isRight = mainPosition === 'right';
    const isLeft = mainPosition === 'left';
    const isStart = secondPosition === 'start';
    const isEnd = secondPosition === 'end';
    return (
        <div
            className={clsx(
                'absolute',
                {
                    '-bottom-1.5 mx-1': isTop,
                    '-top-1.5 mx-1': isBottom,
                    '-right-3 my-1': isLeft,
                    '-left-3 my-1': isRight,
                    'left-8': (isBottom || isTop) && isStart,
                    'right-8': (isBottom || isTop) && isEnd,
                    'top-12': (isLeft || isRight) && isStart,
                    'bottom-12': (isLeft || isRight) && isEnd
                },
                customClassName
            )}>
            <div
                data-testid="tooltip-arrow"
                style={arrowStyle[mainPosition]}
                className={clsx('h-1.5 w-3 text-icoGray-300 dark:text-icoDarkMode-beton', {
                    invisible: !isBorder
                })}
                data-popper-arrow={position}
            />
            <div
                data-testid="tooltip-arrow"
                style={arrowStyle[mainPosition]}
                className={clsx('absolute h-1.5 w-3', arrowVariant[variant], {
                    'left-px top-0': isRight,
                    'right-px top-0': isLeft,
                    'top-px': isBottom,
                    'bottom-px': isTop
                })}
                data-popper-arrow={position}
            />
        </div>
    );
};

const closeStyles = {
    white: 'bg-white dark:bg-icoDarkMode-anthracite text-icoGray-500 dark:text-white',
    lightGray: 'bg-icoGray-300 dark:bg-icoDarkMode-deepBlack text-icoGray-500 dark:text-white'
} as const;

export type CustomPopoverProps = ComponentPropsWithoutRef<'div'> & {
    setPopperElement: Dispatch<SetStateAction<HTMLDivElement>>;
    popperElement: Parameters<typeof usePopper>[1];
    toggle: () => void;
    open: boolean;
    styles: ReturnType<typeof usePopper>['styles'];
    attributes: ReturnType<typeof usePopper>['attributes'];
    closeVariant?: keyof typeof closeStyles;
    hasBtnClose?: boolean;
    hasArrow?: boolean;
    isBorder?: boolean;
    width?: string;
    wrapperClassName?: string;
    disableClickOuter?: boolean;
    customArrowClassName?: string;
    customBtnCloseClassName?: string;
};

const CustomPopover = ({
    setPopperElement,
    popperElement,
    toggle,
    open,
    styles,
    attributes,
    closeVariant = 'white',
    hasBtnClose = true,
    hasArrow = true,
    isBorder = false,
    width = 'w-80',
    wrapperClassName,
    disableClickOuter = false, // Useful if you have a modal open at the same time as this popover,
    customArrowClassName,
    customBtnCloseClassName,
    ...restProps
}: CustomPopoverProps) => {
    const clickOuter = useCallback(
        (e: Event) => {
            if (popperElement?.contains(e.target as Node) || disableClickOuter) {
                return;
            }

            toggle();
        },
        [popperElement, toggle, disableClickOuter]
    );

    useEffect(() => {
        document.addEventListener('mousedown', clickOuter);

        return () => {
            document.removeEventListener('mousedown', clickOuter);
        };
    }, [clickOuter]);

    return (
        <div
            className={wrapperClassName}
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}>
            <Transition
                show={open}
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1">
                <div className={clsx('relative', width)}>
                    {hasArrow && (
                        <Arrow
                            position={
                                attributes?.popper?.[
                                    'data-popper-placement'
                                ] as PopperProps<unknown>['placement']
                            }
                            isBorder={isBorder}
                            customClassName={customArrowClassName}
                        />
                    )}
                    {hasBtnClose ? (
                        <button
                            aria-label="close"
                            onClick={toggle}
                            className={clsx(
                                'absolute -top-6 flex h-5 w-5 items-center justify-center rounded-full text-xs',
                                closeStyles[closeVariant],
                                {
                                    'left-0':
                                        attributes?.popper?.['data-popper-placement'].includes(
                                            'left'
                                        ),
                                    'right-0':
                                        !attributes?.popper?.['data-popper-placement'].includes(
                                            'left'
                                        )
                                },
                                customBtnCloseClassName
                            )}>
                            <Icon icon="close" />
                        </button>
                    ) : null}
                    <div
                        className={clsx(
                            'rounded-md bg-white shadow-icoM dark:bg-icoDarkMode-anthracite',
                            {
                                'border border-icoGray-300 dark:border-icoDarkMode-beton': isBorder
                            }
                        )}
                        {...restProps}
                    />
                </div>
            </Transition>
        </div>
    );
};

export default CustomPopover;
